import { NotificationOpenResult } from '@core/services/notifications';

/**
 * Where an opened notification takes the user in this app. The backend sends the record
 * reference (module + id) rather than a URL; routing stays a frontend concern.
 *
 * Approval notifications go to the module's approval screen, else the approver's pending
 * queue. Status updates go to the
 * requester's own request screen. Announcements and polls are shown (and voted on) on the
 * dashboard. Modules with no screen yet return null, and the tray says so instead of
 * navigating to a dead route.
 */
const REQUEST_ROUTES: Record<string, (id: string) => string> = {
  LEAVE: (id) => `/requests/leave/${id}`,
  'LEAVE RESUMPTION': (id) => `/requests/leave-resumption/${id}`,
  'LOAN REQUEST': (id) => `/requests/loan/${id}`,
  'LOAN ADJUSTMENT': (id) => `/requests/loan-adjustment/${id}`,
  TRAVEL: (id) => `/requests/travel/${id}`,
  'TRAVEL EXPENSE': (id) => `/requests/expense/${id}`,
  'LEAVE ENCASHMENT': () => '/requests/encashment',
  'ATTENDANCE REGULARIZATION': () => '/requests/attendance-regularization',
  LETTER: () => '/requests/letter',
  ASSET: () => '/requests/asset',
  DOCUMENT: () => '/requests/document',
  TIMESHEET: () => '/tna/timesheet',
};

/** Modules whose approvers act on a dedicated screen rather than the shared pending queue. */
const APPROVAL_ROUTES: Record<string, string> = {
  LEAVE: '/approve/leave',
  'LEAVE ENCASHMENT': '/approve/leave-encashment',
  'LEAVE RESUMPTION': '/approve/leave-resumption',
  'ATTENDANCE REGULARIZATION': '/approve/attendance-regularization',
  DOCUMENT: '/approve/document',
  TRAVEL: '/approve/travel',
  'TRAVEL EXPENSE': '/approve/expense',
  'LOAN REQUEST': '/approve/loan',
  'LOAN ADJUSTMENT': '/approve/loan-adjustment',
  TIMESHEET: '/approve/approve-timesheets',
};

export const APPROVAL_QUEUE_ROUTE = '/ess/pending-request';
export const DASHBOARD_ROUTE = '/dashboard';

export function notificationDestination(destination: NotificationOpenResult['destination']): string | null {
  const { notificationType, referenceModule, referenceId } = destination;
  switch (notificationType) {
    case 'APPROVAL':
      return (referenceModule && APPROVAL_ROUTES[referenceModule]) || APPROVAL_QUEUE_ROUTE;
    case 'REQUEST_STATUS_UPDATE': {
      const route = referenceModule ? REQUEST_ROUTES[referenceModule] : undefined;
      return route && referenceId ? route(referenceId) : null;
    }
    case 'ANNOUNCEMENT':
    case 'POLL':
      return DASHBOARD_ROUTE;
    default:
      return null;
  }
}
